import { useEffect, useRef, useState } from 'react';
import { Container, Form, FormControl } from 'react-bootstrap';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { Topbar } from '../../components/Topbar/Topbar';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { useHistory } from 'react-router-dom';
import './Search.scss';
import { useQuery } from '../../../hooks/useQuery';
import { useTranslation } from 'react-i18next';
import { Title } from '../../components/Title/Title';
import { useCurrentLanguage } from '../../../hooks/useCurrentLanguage';
import algoliasearch from 'algoliasearch';
import { VideoCard } from '../../components/VideoCard/VideoCard';

const SearchComponent = () => {
  const { currentUser, userDataFmt, getVideoCardsByIds, getCachedVideoCardsByIds } = useFirebase();

  const [results, setResults] = useState<any[]>([]);
  const [resultIds, setResultIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [expectedResultsCount, setExpectedResultsCount] = useState(0);
  const [time, setTime] = useState(0);
  const [algoliaIndex, setAlgoliaIndex] = useState<ReturnType<ReturnType<typeof algoliasearch>['initIndex']>>(null);
  const [algoliaIndexName, setAlgoliaIndexName] = useState<string>(null);
  const [searchReady, setSearchReady] = useState(false);

  const { q } = useQuery();
  const [search, setSearch] = useState(q || '');
  const history = useHistory();
  const { t } = useTranslation();
  const { currentLanguage } = useCurrentLanguage();

  const searchSubject = useRef<BehaviorSubject<any>>(new BehaviorSubject(''));
  const latestSearchRequestId = useRef(0);

  useEffect(() => {
    if (searchReady) return;
    if (!userDataFmt) return;
    if (!algoliaIndex) return;
    if (!algoliaIndexName) return;
    setSearchReady(true);
  }, [searchReady, userDataFmt, algoliaIndex, algoliaIndexName]);

  useEffect(() => {
    const client = algoliasearch(process.env.REACT_APP_ALGOLIA_APP_ID!, process.env.REACT_APP_ALGOLIA_KEY!);
    client.listIndices().then((indices) => {
      const indexName = (() => {
        const localizedIndex = `${process.env.REACT_APP_ALGOLIA_INDEX_NAME}_${currentLanguage}`;
        if (indices.items.find((i) => i.name === localizedIndex)) return localizedIndex;
        return process.env.REACT_APP_ALGOLIA_INDEX_NAME;
      })();
      setAlgoliaIndexName(indexName);
      setAlgoliaIndex(client.initIndex(indexName));
    });
  }, [currentLanguage]);

  useEffect(() => {
    history.replace({
      pathname: window.location.pathname,
      search: `q=${search}`,
    });
  }, [search]);

  useEffect(() => {
    if (!searchReady) return;
    const sub = searchSubject.current
      .pipe(
        tap(() => {
          setLoading(true);
          setResults([]);
          setResultIds([]);
          setExpectedResultsCount(0);
        }),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap<string, Promise<{ time: number; data: any[] }>>((term) => {
          if (!term) {
            return Promise.resolve({ time: 0, data: [] });
          }
          return algoliaIndex
            .search(term, {
              facetFilters: ['visible:true', `visibleFor.${userDataFmt.profile.formData.categoriaProfessionale}:true`],
              hitsPerPage: 1000,
              userToken: currentUser.uid!,
              clickAnalytics: true,
              headers: { 'X-Algolia-UserToken': currentUser.uid! },
            })
            .then(async (response: any) => {
              const requestId = ++latestSearchRequestId.current;
              const ids = (response.hits || []).map((hit) => hit.objectID);
              setResultIds(ids);
              setExpectedResultsCount(ids.length);
              if (!ids.length) return { time: response.serverTimeMS || 0, data: [] };

              const cachedItems = getCachedVideoCardsByIds(ids);
              const cachedItemsById = new Map(cachedItems.map((video) => [video.id, video]));
              const buildResults = (items: any[]) => {
                const itemsById = new Map(items.map((video) => [video.id, video]));
                return ids.map((id) => {
                  const video = itemsById.get(id);
                  if (!video) return null;
                  return { ...video, queryID: video.id, index: algoliaIndexName };
                });
              };

              const cachedResults = buildResults(cachedItems);
              if (requestId === latestSearchRequestId.current) {
                setResults(cachedResults);
              }

              const missingIds = ids.filter((id) => !cachedItemsById.has(id));
              if (!missingIds.length) {
                return { time: response.serverTimeMS || 0, data: cachedResults };
              }

              const { items = [] } = await getVideoCardsByIds(missingIds);
              const mergedItems = [...cachedItems, ...items];
              return {
                time: response.serverTimeMS || 0,
                data: buildResults(mergedItems),
              };
            });
        })
      )
      .subscribe((response: any) => {
        setResults(response.data || []);
        setTime(response.time || 0);
        setLoading(false);
      });

    return () => {
      sub.unsubscribe();
    };
  }, [searchReady]);

  useEffect(() => {
    searchSubject.current.next(search);
  }, [search]);

  if (!searchReady) return <FullscreenLoader />;

  return (
    <>
      <Title>{t('labels.cerca')}</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <div className={'d-flex justify-content-center'}>
          <Form.Group className={'mb-3'} style={{ width: '100%', maxWidth: '500px' }}>
            <FormControl size={'lg'} autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`${t('labels.cerca')}...`} />
            {!loading && !!resultIds.length && !!search && (
              <Form.Text className={'text-muted text-right'}>
                {expectedResultsCount} {expectedResultsCount == 1 ? t('search.labels.risultato') : t('search.labels.risultati')} {t('search.labels.in')}{' '}
                {(time / 1000).toFixed(3)}s
              </Form.Text>
            )}
          </Form.Group>
        </div>

        {loading && !!search && !resultIds.length && <FullscreenLoader fullscreen={false} />}

        {!!resultIds.length && (
          <div className={'search-results'}>
            {resultIds.map((id, index) => {
              const video = results[index];
              if (!video) return <VideoCard empty={true} className={'m-1'} key={`skeleton-${id}-${index}`} />;

              return (
                <VideoCard
                  className={'m-1'}
                  key={video.id.toString()}
                  index={index}
                  video={video}
                  onClick={() => {
                    try {
                      (window as any).aa('clickedObjectIDsAfterSearch', {
                        userToken: currentUser.uid,
                        eventName: 'PLP;clickedObjectIDsAfterSearch',
                        index: video.index,
                        queryID: video.queryID,
                        objectIDs: [video.id],
                        positions: [index + 1],
                      });
                    } catch (e) {
                      console.error(e);
                    }
                  }}
                />
              );
            })}
          </div>
        )}

        {!loading && !!search && !resultIds.length && <div className="text-center mt-4">{t('search.labels.noRisultati')}</div>}
      </Container>

      <Footer />
    </>
  );
};

export default SearchComponent;
